import { rankBetween } from "@plannello/core/ranking";
import { m } from "@plannello/i18n/messages";
import type {
  CommentView,
  ItemCreate,
  ItemDetail,
  ItemLite,
  ItemPatch,
  ItemTombstone,
  RelationKind,
} from "@plannello/shared";
import { ApiError, api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { queryClient } from "@/query-client";
import { getCatalog } from "./CatalogProvider";
import { whenOnline } from "./live/online";
import {
  applyItemRemoval,
  applyItemUpsert,
  CREATE_MARK,
  cancelLiteFetches,
  findLite,
  markLocalWrite,
  refreshDetail,
} from "./store";
import { pushUndo } from "./undo";

/**
 * Optimistic writes. Each one: snapshot, apply locally through the store, send, then reconcile
 * with the server row (or roll back). Writes to the same item are serialized so a quick series of
 * edits reaches the server in order.
 *
 * Concurrency policy:
 *   - Property changes (status, priority, type, assignees, labels, rank, parent, milestone) are
 *     field-level last-write-wins and set ops, so they never send `expectedVersion`. Decision after
 *     live sync (M4): kept on purpose. The caches are fresh now, but `version` counts every change
 *     to the item, so a version check would reject "set status" because somebody else (or Claude)
 *     changed the priority or added a label a second earlier: a spurious 409 for fields that do not
 *     conflict. The same applies to a quick series of edits of our own, whose `before.version` is
 *     stale by the time the second one is sent. Set operations (labels, assignees) merge, and the
 *     worst case for scalar fields is that the later write wins, which the activity log records.
 *   - Title edits send `expectedVersion`; a 409 reloads the row and says so.
 *   - Descriptions send `descriptionBaseVersion`; a 409 is returned to the editor, which shows the
 *     merge banner (see DescriptionSection). Live sync feeds the same banner when the description
 *     changes remotely while the editor is open.
 *
 * Offline: every network write goes through `whenOnline` (live/online.ts). While the browser is
 * offline the optimistic change stays on screen and the request waits; it is sent, in order, when
 * the browser is back. Writes to one item stay serialized, so per-item order is kept.
 *
 * Live sync (live/apply.ts) defers remote events for an item while one of its writes is pending
 * (`hasPendingWrite` / `afterWrites`) and recognises the echo of our own writes (`markLocalWrite`).
 */

const queues = new Map<string, Promise<unknown>>();
function serial<T>(id: string, fn: () => Promise<T>): Promise<T> {
  const prev = queues.get(id) ?? Promise.resolve();
  const next = prev.catch(() => undefined).then(fn);
  queues.set(id, next);
  void next.finally(() => {
    if (queues.get(id) === next) queues.delete(id);
  });
  return next;
}

/**
 * Archived products are read-only (the server refuses writes with `product_archived`). The views hide their
 * editing controls; this is the net under them for keyboard shortcuts, drag and drop and stale screens: it says
 * so and stops before any optimistic change or request. Returns true when the write must not happen.
 */
function refuseArchived(productIds: Iterable<string | undefined>): boolean {
  const catalog = getCatalog();
  if (!catalog) return false;
  for (const id of productIds) {
    if (id && catalog.isArchived(id)) {
      toast.show({ title: m.lifecycle_readonly_toast(), tone: "danger" });
      return true;
    }
  }
  return false;
}

/** True while a write to this item is queued or in flight (live events for it wait). */
export const hasPendingWrite = (id: string) => queues.has(id);

/** Resolves when every write queued for this item so far has settled. */
export async function afterWrites(id: string): Promise<void> {
  while (queues.has(id)) await queues.get(id)?.catch(() => undefined);
}

const uniq = <T>(xs: T[]) => [...new Set(xs)];

/** Applies a patch to a lite row the way the server will (for optimistic display). */
export function applyPatchLocally(item: ItemLite, patch: ItemPatch): ItemLite {
  const next: ItemLite = { ...item, updatedAt: new Date().toISOString() };
  if (patch.title !== undefined) next.title = patch.title;
  if (patch.statusId !== undefined) {
    next.statusId = patch.statusId;
    next.statusChangedAt = next.updatedAt;
  }
  if (patch.priority !== undefined) next.priority = patch.priority;
  if (patch.typeId !== undefined) next.typeId = patch.typeId;
  if (patch.parentId !== undefined) next.parentId = patch.parentId;
  if (patch.milestoneId !== undefined) next.milestoneId = patch.milestoneId;
  if (patch.estimate !== undefined) next.estimate = patch.estimate;
  if (patch.startDate !== undefined) next.startDate = patch.startDate;
  if (patch.dueDate !== undefined) next.dueDate = patch.dueDate;
  if (patch.rank !== undefined) next.rank = patch.rank;
  if (patch.contentLanguage !== undefined) {
    // A manual choice sticks; back to automatic keeps the detected language until the server answers.
    if (patch.contentLanguage) next.contentLanguage = patch.contentLanguage;
    next.contentLanguageSource = patch.contentLanguage ? "manual" : "auto";
  }
  if (patch.labels) {
    const remove = new Set(patch.labels.remove ?? []);
    next.labelIds = uniq([...item.labelIds.filter((l) => !remove.has(l)), ...(patch.labels.add ?? [])]);
  }
  if (patch.assignees) {
    const remove = new Set(patch.assignees.remove ?? []);
    next.assigneeIds = uniq([...item.assigneeIds.filter((u) => !remove.has(u)), ...(patch.assignees.add ?? [])]);
  }
  if (patch.fields) {
    const fields = { ...item.fields };
    for (const [k, v] of Object.entries(patch.fields)) {
      if (v === null) delete fields[k];
      else fields[k] = v;
    }
    next.fields = fields;
  }
  return next;
}

/** The patch that turns `after` back into `before` for the fields `patch` touched. */
export function inversePatch(before: ItemLite, patch: ItemPatch): ItemPatch {
  const inv: ItemPatch = {};
  if (patch.title !== undefined) inv.title = before.title;
  if (patch.statusId !== undefined) inv.statusId = before.statusId;
  if (patch.priority !== undefined) inv.priority = before.priority;
  if (patch.typeId !== undefined) inv.typeId = before.typeId;
  if (patch.parentId !== undefined) inv.parentId = before.parentId;
  if (patch.milestoneId !== undefined) inv.milestoneId = before.milestoneId;
  if (patch.estimate !== undefined) inv.estimate = before.estimate;
  if (patch.startDate !== undefined) inv.startDate = before.startDate;
  if (patch.dueDate !== undefined) inv.dueDate = before.dueDate;
  if (patch.rank !== undefined || patch.placement !== undefined) inv.rank = before.rank;
  if (patch.contentLanguage !== undefined) {
    inv.contentLanguage = before.contentLanguageSource === "manual" ? (before.contentLanguage ?? null) : null;
  }
  if (patch.labels) {
    inv.labels = {
      add: (patch.labels.remove ?? []).filter((l) => before.labelIds.includes(l)),
      remove: (patch.labels.add ?? []).filter((l) => !before.labelIds.includes(l)),
    };
  }
  if (patch.assignees) {
    inv.assignees = {
      add: (patch.assignees.remove ?? []).filter((u) => before.assigneeIds.includes(u)),
      remove: (patch.assignees.add ?? []).filter((u) => !before.assigneeIds.includes(u)),
    };
  }
  if (patch.fields) {
    inv.fields = Object.fromEntries(Object.keys(patch.fields).map((k) => [k, before.fields[k] ?? null]));
  }
  return inv;
}

const isEmptyPatch = (p: ItemPatch) =>
  Object.entries(p).every(
    ([, v]) =>
      v === undefined ||
      (typeof v === "object" &&
        v !== null &&
        "add" in v &&
        !(v as { add?: unknown[] }).add?.length &&
        !(v as { remove?: unknown[] }).remove?.length),
  );

function conflictRow(e: unknown): ItemLite | null {
  if (!(e instanceof ApiError) || !e.isConflict) return null;
  const current = e.details?.current as ItemLite | undefined;
  return current ?? null;
}

async function sendPatch(item: ItemLite, patch: ItemPatch): Promise<ItemLite> {
  return whenOnline(() => api.patch<ItemLite>(`/api/v1/items/${item.id}`, patch));
}

export type UpdateOptions = {
  /** Toast title with an Undo action; omit for silent writes (e.g. the undo itself). */
  undoTitle?: string;
  /** Send `expectedVersion` (title edits). */
  versioned?: boolean;
};

/**
 * Patches one or many items optimistically. `build` returns the patch per item (return null to
 * skip it). One undo restores every item of the batch.
 */
export async function updateItems(
  targets: readonly ItemLite[],
  build: (item: ItemLite) => ItemPatch | null,
  opts: UpdateOptions = {},
): Promise<void> {
  const work = targets
    .map((t) => {
      const current = findLite(t.id) ?? t;
      const patch = build(current);
      return patch && !isEmptyPatch(patch) ? { before: current, patch } : null;
    })
    .filter((w): w is { before: ItemLite; patch: ItemPatch } => w !== null);
  if (work.length === 0) return;
  if (refuseArchived(work.map((w) => w.before.productId))) return;

  await cancelLiteFetches(work.map((w) => w.before.productId));
  for (const w of work) applyItemUpsert(applyPatchLocally(w.before, w.patch));

  const undo = () =>
    void updateItems(
      work.map((w) => findLite(w.before.id) ?? w.before),
      (item) => {
        const w = work.find((x) => x.before.id === item.id);
        return w ? inversePatch(w.before, w.patch) : null;
      },
    );
  if (opts.undoTitle) {
    toast.undo(opts.undoTitle, pushUndo(undo), m.common_undo());
  }

  let conflicted = false;
  let failed = false;
  let forbidden = false;
  await Promise.all(
    work.map((w) =>
      serial(w.before.id, async () => {
        const live = findLite(w.before.id) ?? w.before;
        const body: ItemPatch = opts.versioned ? { ...w.patch, expectedVersion: w.before.version } : w.patch;
        markLocalWrite(live.id);
        try {
          const saved = await sendPatch(live, body);
          markLocalWrite(live.id);
          applyItemUpsert(saved);
          refreshDetail(saved.id);
        } catch (e) {
          const current = conflictRow(e);
          if (current) {
            conflicted = true;
            applyItemUpsert(current);
          } else {
            if (e instanceof ApiError && e.status === 403) forbidden = true;
            else failed = true;
            applyItemUpsert(w.before);
          }
        }
      }),
    ),
  );
  if (conflicted) toast.show({ title: m.toast_conflict_reloaded() });
  if (forbidden) toast.show({ title: m.toast_forbidden(), tone: "danger" });
  else if (failed) toast.show({ title: m.toast_update_failed(), tone: "danger" });
}

/** Rank between two neighbours (either may be missing). Falls back to the end on equal ranks. */
export function rankBetweenItems(before: ItemLite | undefined, after: ItemLite | undefined): string {
  const a = before?.rank ?? null;
  const b = after?.rank ?? null;
  try {
    return rankBetween(a, b);
  } catch {
    return rankBetween(a, null);
  }
}

export type CreateResult = { item: ItemLite };

/**
 * Creates an item. The row appears at once with a placeholder key (`NWS-…`) and is swapped for
 * the server row when it arrives.
 */
export async function createItem(input: ItemCreate, productKey: string, meId: string | null): Promise<ItemLite> {
  const tempId = crypto.randomUUID();
  const now = new Date().toISOString();
  const placeholder: ItemLite = {
    id: tempId,
    key: `${productKey}-…`,
    productId: input.productId,
    spaceId: "",
    number: 0,
    typeId: input.typeId ?? "",
    statusId: input.statusId ?? "",
    priority: input.priority ?? 0,
    title: input.title,
    parentId: input.parentId ?? null,
    milestoneId: input.milestoneId ?? null,
    rank: input.rank ?? "~",
    assigneeIds: input.assigneeIds ?? [],
    labelIds: input.labelIds ?? [],
    blockedByIds: [],
    estimate: input.estimate ?? null,
    startDate: input.startDate ?? null,
    dueDate: input.dueDate ?? null,
    fields: input.fields ?? {},
    reporterId: meId,
    createdById: meId,
    createdAt: now,
    updatedAt: now,
    statusChangedAt: now,
    startedAt: null,
    completedAt: null,
    archivedAt: null,
    version: 0,
  };
  // Only show the placeholder when we know its status and type (otherwise the server picks defaults).
  const showPlaceholder = !!input.typeId && !!input.statusId;
  if (showPlaceholder) {
    await cancelLiteFetches([input.productId]);
    applyItemUpsert(placeholder);
  }
  markLocalWrite(CREATE_MARK);
  try {
    // The key makes the replay after a dropped connection safe: the server answers with the first result.
    const saved = await whenOnline(() =>
      api<ItemLite>("/api/v1/items", { method: "POST", body: input, headers: { "Idempotency-Key": tempId } }),
    );
    markLocalWrite(CREATE_MARK);
    markLocalWrite(saved.id);
    applyItemUpsert(saved, { replaceId: tempId });
    if (saved.parentId) refreshDetail(saved.parentId);
    return saved;
  } catch (e) {
    if (showPlaceholder) applyItemRemoval({ id: tempId });
    throw e;
  }
}

/** True for the optimistic placeholder of an item being created. */
export const isPlaceholder = (item: ItemLite) => item.version === 0;

/** Moves items to the trash with one undo (restore). */
export async function deleteItems(targets: readonly ItemLite[], opts: { silent?: boolean } = {}): Promise<void> {
  if (targets.length === 0) return;
  if (refuseArchived(targets.map((t) => t.productId))) return;
  for (const t of targets) {
    markLocalWrite(t.id);
    applyItemRemoval(t);
  }
  const restore = () => {
    for (const t of targets) {
      markLocalWrite(t.id);
      applyItemUpsert(t);
      void whenOnline(() => api.post<ItemLite>(`/api/v1/items/${t.id}/restore`))
        .then((row) => {
          applyItemUpsert(row);
          refreshDetail(row.id);
        })
        .catch(() => toast.show({ title: m.toast_update_failed(), tone: "danger" }));
    }
  };
  if (!opts.silent) {
    const title =
      targets.length === 1
        ? m.toast_deleted_one({ key: targets[0]!.key })
        : m.toast_deleted_many({ count: targets.length });
    toast.undo(title, pushUndo(restore), m.common_undo());
  }
  const results = await Promise.allSettled(
    targets.map((t) => whenOnline(() => api.delete<ItemTombstone>(`/api/v1/items/${t.id}`))),
  );
  for (const t of targets) markLocalWrite(t.id);
  results.forEach((r, i) => {
    if (r.status === "rejected") applyItemUpsert(targets[i]!);
  });
  if (results.some((r) => r.status === "rejected")) toast.show({ title: m.toast_update_failed(), tone: "danger" });
  for (const t of targets) if (t.parentId) refreshDetail(t.parentId);
}

/** Moves an item to another product (it gets a new key; the old one keeps redirecting). */
export async function moveItemToProduct(item: ItemLite, productId: string): Promise<ItemLite | null> {
  if (refuseArchived([item.productId, productId])) return null;
  markLocalWrite(item.id);
  try {
    const saved = await whenOnline(() => api.post<ItemLite>(`/api/v1/items/${item.id}/move`, { productId }));
    markLocalWrite(item.id);
    applyItemUpsert(saved);
    refreshDetail(saved.id);
    return saved;
  } catch (e) {
    const current = conflictRow(e);
    if (current) {
      applyItemUpsert(current);
      toast.show({ title: m.toast_conflict_reloaded() });
    } else toast.show({ title: m.toast_move_failed(), tone: "danger" });
    return null;
  }
}

/** Appends a comment at once; the server copy replaces it, an error removes it again. */
export async function addComment(itemId: string, bodyMd: string, authorId: string | null): Promise<boolean> {
  if (refuseArchived([findLite(itemId)?.productId])) return false;
  const tempId = crypto.randomUUID();
  const optimistic: CommentView = {
    id: tempId,
    itemId,
    authorId,
    viaUserId: null,
    source: "web",
    bodyMd,
    editedAt: null,
    createdAt: new Date().toISOString(),
  };
  const update = (fn: (list: CommentView[]) => CommentView[]) =>
    queryClient.setQueriesData<ItemDetail>({ queryKey: ["item"] }, (old) =>
      old && old.id === itemId ? { ...old, comments: fn(old.comments) } : old,
    );
  update((list) => [...list, optimistic]);
  markLocalWrite(itemId);
  try {
    const saved = await whenOnline(() =>
      api<CommentView>(`/api/v1/items/${itemId}/comments`, {
        method: "POST",
        body: { bodyMd },
        headers: { "Idempotency-Key": tempId },
      }),
    );
    markLocalWrite(itemId);
    // The live event with the same comment may have been appended already: keep one copy.
    update((list) => list.filter((c) => c.id !== saved.id).map((c) => (c.id === tempId ? saved : c)));
    return true;
  } catch {
    update((list) => list.filter((c) => c.id !== tempId));
    toast.show({ title: m.toast_comment_failed(), tone: "danger" });
    return false;
  }
}

export type DescriptionSave =
  | { ok: true; item: ItemLite }
  | { ok: false; conflict: { theirs: string; descriptionVersion: number; current: ItemLite } }
  | { ok: false; error: true };

/** Saves a description against the version it was based on; a 409 hands back their text. */
export async function saveDescription(itemId: string, md: string, baseVersion: number): Promise<DescriptionSave> {
  if (refuseArchived([findLite(itemId)?.productId])) return { ok: false, error: true };
  markLocalWrite(itemId);
  try {
    const saved = await whenOnline(() =>
      api.patch<ItemLite>(`/api/v1/items/${itemId}`, { descriptionMd: md, descriptionBaseVersion: baseVersion }),
    );
    markLocalWrite(itemId);
    queryClient.setQueriesData<ItemDetail>({ queryKey: ["item"] }, (old) =>
      old && old.id === itemId ? { ...old, ...saved, descriptionMd: md, descriptionVersion: saved.version } : old,
    );
    applyItemUpsert(saved);
    refreshDetail(itemId);
    return { ok: true, item: saved };
  } catch (e) {
    if (e instanceof ApiError && e.isConflict && typeof e.details?.currentDescription === "string") {
      const current = e.details.current as ItemLite;
      const theirs = e.details.currentDescription;
      const descriptionVersion = Number(e.details.descriptionVersion);
      // The cached detail shows their text from now on; the editor keeps ours until the user picks.
      queryClient.setQueriesData<ItemDetail>({ queryKey: ["item"] }, (old) =>
        old && old.id === itemId ? { ...old, descriptionMd: theirs, descriptionVersion } : old,
      );
      applyItemUpsert(current);
      refreshDetail(itemId);
      return {
        ok: false,
        conflict: {
          theirs: e.details.currentDescription,
          descriptionVersion: Number(e.details.descriptionVersion),
          current,
        },
      };
    }
    return { ok: false, error: true };
  }
}

export async function addRelation(itemId: string, to: string, kind: RelationKind): Promise<boolean> {
  if (refuseArchived([findLite(itemId)?.productId, findLite(to)?.productId])) return false;
  markLocalWrite(itemId);
  try {
    await whenOnline(() => api.post(`/api/v1/items/${itemId}/relations`, { to, kind }));
    markLocalWrite(itemId);
    refreshDetail(itemId);
    void queryClient.invalidateQueries({ queryKey: ["lites"] });
    return true;
  } catch (e) {
    toast.show({
      title: e instanceof ApiError && e.reason === "relation_cycle" ? m.relation_cycle() : m.toast_update_failed(),
      tone: "danger",
    });
    return false;
  }
}

export async function removeRelation(itemId: string, relationId: string): Promise<void> {
  if (refuseArchived([findLite(itemId)?.productId])) return;
  queryClient.setQueriesData<ItemDetail>({ queryKey: ["item"] }, (old) =>
    old && old.id === itemId ? { ...old, relations: old.relations.filter((r) => r.id !== relationId) } : old,
  );
  markLocalWrite(itemId);
  try {
    await whenOnline(() => api.delete(`/api/v1/relations/${relationId}`));
    markLocalWrite(itemId);
  } catch {
    toast.show({ title: m.toast_update_failed(), tone: "danger" });
  }
  refreshDetail(itemId);
}
